import type { LinkedinOptimization } from '../types';
import { tBrut as t } from '../i18n';

/**
 * Le kit LinkedIn en UN texte, pour « Copier tout le kit » — fonction PURE (libellés lus à l'appel :
 * une constante de module les figerait dans la langue de l'import).
 *
 * Mêmes sections, mêmes libellés et mêmes conditions d'affichage que `LinkedinOptimizationCard` :
 * ce qui n'est pas à l'écran n'est pas copié. N'en font pas partie : le contrôle qualité, la note de
 * recherche, et les conseils de photo et de bannière — des explications pour la personne, pas du
 * texte à coller dans LinkedIn (la carte ne leur donne d'ailleurs pas de bouton « Copier »).
 *
 * `tBrut` et non `t` : le texte SORT de l'application. `t` préfixe chaque chaîne d'une marque
 * invisible (U+200F) en arabe sur Android, qui serait collée telle quelle dans LinkedIn.
 */
export function texteKit(data: LinkedinOptimization): string {
  const sections: string[] = [];
  const bloc = (label: string, texte: string): string | null => (texte.trim() ? `${label}\n${texte}` : null);
  const liste = (label: string, items: string[]): string | null => (items.length > 0 ? bloc(label, items.join(', ')) : null);
  const section = (titre: string, blocs: (string | null)[]) => {
    const corps = blocs.filter((b): b is string => b !== null);
    if (corps.length > 0) sections.push([titre.toUpperCase(), ...corps].join('\n\n'));
  };

  section(t('linkedinKit.headline'), [
    bloc(t('linkedin.optimisation.v1'), data.titles.v1),
    bloc(t('linkedin.optimisation.v2'), data.titles.v2),
    bloc(t('linkedin.optimisation.v3'), data.titles.v3),
  ]);
  section(t('linkedinKit.about'), [
    bloc(t('linkedinKit.aboutShort'), data.about.short),
    bloc(t('linkedinKit.aboutFull'), data.about.full),
  ]);
  section(
    t('linkedinKit.experiences'),
    data.experiences.map((e, i) => bloc(t('linkedinKit.experienceN', { n: i + 1 }), e.suggestion)),
  );
  section(t('linkedinKit.skills'), [
    liste(t('linkedinKit.skillsPinned'), data.skills.pinned),
    liste(t('linkedinKit.skillsExtra'), data.skills.complementary),
  ]);
  // Même condition que la carte : le groupe n'existe qu'avec des intitulés ou une note.
  const rechercheVisible = data.search.titles.length > 0 || data.search.note.trim() !== '';
  section(t('linkedinKit.search'), !rechercheVisible ? [] : [
    liste(t('linkedinKit.searchTitles'), data.search.titles),
    liste(t('linkedinKit.searchContracts'), data.search.contracts),
    liste(t('linkedinKit.searchLocations'), data.search.locations),
    liste(t('linkedinKit.searchSectors'), data.search.sectors),
    bloc(t('linkedinKit.searchAvailability'), data.search.availability),
  ]);
  const reco = data.express.recommendation;
  section(t('linkedinKit.express'), [
    bloc(t('linkedinKit.url'), data.express.url),
    bloc(t('linkedinKit.education'), data.express.education),
    bloc(reco.who ? t('linkedinKit.recoAskWho', { who: reco.who }) : t('linkedinKit.recoAsk'), reco.message),
  ]);

  return sections.join('\n\n\n');
}
